import { useNavigate } from 'react-router-dom';

import { TaskStatus, TaskView } from '@ndvr/customer-api';

import { DragEndEvent, KanbanBoard, KanbanCard, KanbanCards, KanbanHeader, KanbanProvider } from '@/components/Kanban';
import { titleCase } from 'utils';
import { useHouseholdMap } from 'views/household/household-queries';

import { useUpdateTaskStatus } from '../task-queries';
import { useTaskOverlayState } from '../task-state';
import { TaskCard } from './TaskCard';

interface TaskKanbanViewProps {
    tasks: TaskView[];
    isLoading?: boolean;
}

const taskStatuses = Object.values(TaskStatus);

const columns = taskStatuses.map((status) => ({
    id: status,
    name: titleCase(status),
}));

const transformTaskToKanbanItem = (task: TaskView) => ({
    id: task.taskGUID,
    name: task.title || '',
    column: task.status as string,
    task,
});

export const TaskKanbanView = (props: TaskKanbanViewProps) => {
    const householdMap = useHouseholdMap();
    const updateTaskStatus = useUpdateTaskStatus();
    const { openViewTaskSheet } = useTaskOverlayState();
    const navigate = useNavigate();

    if (props.isLoading) {
        return (
            <div className="flex min-w-[1100px] items-center justify-center py-12">
                <div className="text-muted-foreground">Loading tasks...</div>
            </div>
        );
    }

    const kanbanTasks = props.tasks.map(transformTaskToKanbanItem);

    const handleDragEnd = (event: DragEndEvent) => {
        if (!event.over) {
            return;
        }
        const over = event.over;
        const id = event.active.id;
        const task = kanbanTasks.find((item) => item.id === id)?.task;
        if (!task) return;

        let newStatus: TaskStatus | undefined;

        if (over.data?.current?.type === 'column') {
            newStatus = over.data.current.columnId;
        } else {
            newStatus =
                columns.find((column) => column.id === over.id)?.id ??
                kanbanTasks.find((item) => item.id === over.id)?.task?.status;
        }

        if (newStatus && newStatus !== task.status) {
            updateTaskStatus.mutate({
                task,
                status: newStatus,
            });
            return;
        }
    };

    return (
        <div className="h-full overflow-x-auto">
            <KanbanProvider columns={columns} data={kanbanTasks} onDragEnd={handleDragEnd}>
                {(column) => (
                    <KanbanBoard id={column.id} key={column.id} className="border-transparent bg-white">
                        <KanbanHeader className="border-none">
                            <div className="mb-3 flex items-center justify-between">
                                <h3 className="font-medium capitalize">{column.name}</h3>
                                <span className="bg-muted text-muted-foreground rounded px-2 py-1 text-xs font-medium">
                                    {kanbanTasks.filter((item) => item.column === (column.id as string)).length}
                                </span>
                            </div>
                        </KanbanHeader>
                        <KanbanCards id={column.id}>
                            {(kanbanItem) => (
                                <KanbanCard
                                    className="!border-border-gray !rounded-lg"
                                    column={column.name}
                                    id={kanbanItem.id}
                                    key={kanbanItem.id}
                                    name={kanbanItem.name}
                                >
                                    <TaskCard
                                        task={kanbanItem.task as TaskView}
                                        householdMap={householdMap}
                                        onTitleClick={(task) => {
                                            navigate(`/tasks/${task.taskGUID}`);
                                            openViewTaskSheet(task.taskGUID);
                                        }}
                                    />
                                </KanbanCard>
                            )}
                        </KanbanCards>
                    </KanbanBoard>
                )}
            </KanbanProvider>
        </div>
    );
};
